import React, { useEffect, useState } from "react";
import { Input, Modal, Spin, Switch } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  ApiAlertRule,
  ApiScorecard,
  deleteAlert,
  listAlerts,
  updateAlert,
  updateScorecard,
} from "./balancedScorecard.service";
import AlertEditorModal from "./AlertEditorModal";
import "./AlertEditorModal.css";

interface ScorecardSettingsModalProps {
  open: boolean;
  scorecard: ApiScorecard;
  canManage: boolean;
  onClose: () => void;
  onChanged: () => void;
}

const ScorecardSettingsModal: React.FC<ScorecardSettingsModalProps> = ({
  open,
  scorecard,
  canManage,
  onClose,
  onChanged,
}) => {
  const { t } = useTranslation("balancedScorecard");
  const [title, setTitle] = useState(scorecard.title);
  const [requireApproval, setRequireApproval] = useState(
    scorecard.require_approval_before_publishing ?? false,
  );
  const [alerts, setAlerts] = useState<ApiAlertRule[]>([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState<{
    open: boolean;
    rule: ApiAlertRule | null;
  }>({ open: false, rule: null });

  const loadAlerts = async () => {
    setLoadingAlerts(true);
    try {
      setAlerts(await listAlerts(scorecard.id));
    } catch {
      toast.error(t("alerts.loadFailed"));
    } finally {
      setLoadingAlerts(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setTitle(scorecard.title);
    setRequireApproval(scorecard.require_approval_before_publishing ?? false);
    loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scorecard.id]);

  // Optimistic toggle: flip the card now, revert if the server refuses.
  const handleToggle = async (rule: ApiAlertRule, enabled: boolean) => {
    setAlerts((list) =>
      list.map((a) => (a.id === rule.id ? { ...a, is_enabled: enabled } : a)),
    );
    try {
      await updateAlert(scorecard.id, rule.id, { is_enabled: enabled });
    } catch {
      setAlerts((list) =>
        list.map((a) =>
          a.id === rule.id ? { ...a, is_enabled: rule.is_enabled } : a,
        ),
      );
      toast.error(t("alerts.updateFailed"));
    }
  };

  const handleDelete = (rule: ApiAlertRule) => {
    Modal.confirm({
      title: t("alerts.deleteTitle"),
      content: t("alerts.deleteBody"),
      okText: t("alerts.delete"),
      okButtonProps: { danger: true },
      cancelText: t("alerts.cancel"),
      onOk: async () => {
        try {
          await deleteAlert(scorecard.id, rule.id);
          toast.success(t("alerts.deleted"));
          await loadAlerts();
        } catch {
          toast.error(t("alerts.deleteFailed"));
        }
      },
    });
  };

  // Turning the restriction on is immediate. Turning it off asks first.
  const handleApprovalSwitch = (checked: boolean) => {
    if (checked) {
      setRequireApproval(true);
      return;
    }
    Modal.confirm({
      title: t("publishing.turnOffTitle"),
      content: t("publishing.turnOffBody"),
      okText: t("publishing.turnOff"),
      cancelText: t("publishing.cancel"),
      onOk: () => setRequireApproval(false),
    });
  };

  const handleSave = async () => {
    const newTitle = title.trim();
    if (!newTitle) {
      toast.error(t("alerts.emptyName"));
      return;
    }
    const titleChanged = newTitle !== scorecard.title;
    const approvalChanged =
      requireApproval !== (scorecard.require_approval_before_publishing ?? false);
    if (!titleChanged && !approvalChanged) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await updateScorecard(scorecard.id, {
        ...(titleChanged ? { title: newTitle } : {}),
        ...(approvalChanged
          ? { require_approval_before_publishing: requireApproval }
          : {}),
      });
      toast.success(t("alerts.settingsSaved"));
      onChanged();
      onClose();
    } catch {
      toast.error(t("alerts.settingsSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        title={t("alerts.settingsTitle")}
        onCancel={onClose}
        width={520}
        footer={null}
        destroyOnClose
        className="bsc-settings-modal"
      >
        <div className="bsc-settings-body">
          <div className="bsc-settings-section-title">
            {t("alerts.general")}
          </div>
          <label className="bsc-alert-label">
            {t("alerts.scorecardName")}
          </label>
          <Input
            value={title}
            disabled={!canManage}
            onChange={(e) => setTitle(e.target.value)}
          />

          <div className="bsc-settings-section-title">
            {t("publishing.title")}
          </div>
          <div className="bsc-settings-toggle-row">
            <div>
              <div className="bsc-settings-toggle-label">
                {t("publishing.requireApproval")}
              </div>
              <div className="bsc-settings-toggle-desc">
                {t("publishing.requireApprovalDesc")}
              </div>
            </div>
            <Switch
              checked={requireApproval}
              disabled={!canManage}
              onChange={handleApprovalSwitch}
            />
          </div>

          <div className="bsc-settings-section-head">
            <div className="bsc-settings-section-title">
              {t("alerts.alertsTitle")}
            </div>
            <button
              type="button"
              className="bsc-alert-link"
              onClick={() => setEditor({ open: true, rule: null })}
              disabled={!canManage}
            >
              <PlusOutlined /> {t("alerts.addAlert")}
            </button>
          </div>

          {loadingAlerts && alerts.length === 0 ? (
            <div className="bsc-alert-loading">
              <Spin />
            </div>
          ) : (
            alerts.map((rule) => (
              <div
                key={rule.id}
                className={`bsc-alert-card ${rule.is_enabled ? "" : "is-off"}`}
              >
                <div className="bsc-alert-card-head">
                  <div>
                    <div className="bsc-alert-card-title">{rule.name}</div>
                    <div className="bsc-alert-card-type">
                      {t(`alerts.types.${rule.alert_type}`)}
                      {rule.is_default && (
                        <span className="bsc-alert-default">
                          {t("alerts.default")}
                        </span>
                      )}
                    </div>
                  </div>
                  <Switch
                    size="small"
                    checked={rule.is_enabled}
                    disabled={!canManage}
                    onChange={(checked) => handleToggle(rule, checked)}
                  />
                </div>

                <dl className="bsc-alert-card-rows">
                  {rule.summary.watches && (
                    <>
                      <dt>{t("alerts.watches")}</dt>
                      <dd>{rule.summary.watches}</dd>
                    </>
                  )}
                  {rule.summary.when && (
                    <>
                      <dt>{t("alerts.when")}</dt>
                      <dd>{rule.summary.when}</dd>
                    </>
                  )}
                  <dt>{t("alerts.notifies")}</dt>
                  <dd>{rule.summary.notifies}</dd>
                  <dt>{t("alerts.via")}</dt>
                  <dd>{rule.summary.via}</dd>
                </dl>

                <div className="bsc-alert-card-foot">
                  {rule.can_delete ? (
                    <button
                      type="button"
                      className="bsc-alert-link bsc-alert-link--danger"
                      onClick={() => handleDelete(rule)}
                      disabled={!canManage}
                    >
                      <DeleteOutlined /> {t("alerts.delete")}
                    </button>
                  ) : (
                    <span />
                  )}
                  <button
                    type="button"
                    className="bsc-alert-link"
                    onClick={() => setEditor({ open: true, rule })}
                    disabled={!canManage}
                  >
                    <EditOutlined /> {t("alerts.edit")}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="bsc-dim-footer">
          <button type="button" className="bsc-btn-cancel" onClick={onClose}>
            {t("alerts.cancel")}
          </button>
          <button
            type="button"
            className="bsc-dim-save"
            onClick={handleSave}
            disabled={!canManage || saving}
          >
            {saving && <Spin size="small" />} {t("alerts.saveChanges")}
          </button>
        </div>
      </Modal>

      <AlertEditorModal
        open={editor.open}
        scorecardId={scorecard.id}
        rule={editor.rule}
        onClose={() => setEditor({ open: false, rule: null })}
        onSaved={loadAlerts}
      />
    </>
  );
};

export default ScorecardSettingsModal;
